'use client';

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Header from '../components/Header';
import Leftnav from '../components/Leftnav';
import Rightchat from '../components/Rightchat';
import Appfooter from '../components/Appfooter';

import { fetchMostViewedPosts } from '../redux/slices/postSlice';

type PostItem = {
  post_id: number;
  client_id?: number | null;
  client_user_id?: number | null;
  main_category?: string | null;
  target_amount?: string | null;
  title?: string | null;
  short_description?: string | null;
  category_id?: number | null;
  category_name?: string | null;
  post_image?: string | null;
  post_video?: string | null;
  youtube_url?: string | null;
  post_type?: string | null;
  created_at?: string | null;
  client_name?: string | null;
  total_collected_amount?: string | null;
  today_collected_amount?: string | null;
  view_count?: number | string | null;
};

type RootState = {
  posts: {
    mostViewedPosts?: PostItem[];
    mostViewedLoading?: boolean;
    error?: string | null;
  };
};

const API_BASE_URL = (
  process.env.REACT_APP_API_URL ||
  'https://api.bsangam.com'
)
  .replace(/\/uploads\/?$/i, '')
  .replace(/\/+$/, '');

const FALLBACK_IMAGE = '/assets/images/story.png';

const getYoutubeThumbnail = (
  url?: string | null
): string => {
  if (!url) return '';

  const match = url.match(
    /(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtube\.com\/embed\/|youtu\.be\/)([^&?/]+)/i
  );

  return match?.[1]
    ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`
    : '';
};

const getUploadUrl = (
  file?: string | null,
  youtubeUrl?: string | null
): string => {
  const normalizedFile = String(
    file || ''
  ).trim();

  if (normalizedFile) {
    if (
      normalizedFile.startsWith('http://') ||
      normalizedFile.startsWith('https://')
    ) {
      return normalizedFile.replace(
        /^http:\/\/www\.api\.bsangam\.com/i,
        'https://api.bsangam.com'
      );
    }

    const cleanPath = normalizedFile.startsWith('/')
      ? normalizedFile
      : `/${normalizedFile}`;

    return `${API_BASE_URL}${cleanPath}`;
  }

  const youtubeThumbnail =
    getYoutubeThumbnail(youtubeUrl);

  if (youtubeThumbnail) {
    return youtubeThumbnail;
  }

  return FALLBACK_IMAGE;
};

const getUserId = (): number | null => {
  try {
    const userData = JSON.parse(
      localStorage.getItem('user') || '{}'
    );

    const userId = Number(
      userData?.user_id
    );

    return Number.isInteger(userId) &&
      userId > 0
      ? userId
      : null;
  } catch {
    return null;
  }
};

const formatPostDate = (
  dateValue?: string | null
): string => {
  if (!dateValue) return '';

  const parsedDate = new Date(dateValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return parsedDate.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const SKELETON_COUNT = 6;

const Defaultstorie: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    mostViewedPosts = [],
    mostViewedLoading = false,
    error = null,
  } = useSelector(
    (state: RootState) => state.posts || {}
  );

  const [search, setSearch] = useState('');

  useEffect(() => {
    const userId = getUserId();

    dispatch(
      fetchMostViewedPosts(userId) as any
    );
  }, [dispatch]);

  const filteredPosts = useMemo(() => {
    const normalizedSearch = search
      .trim()
      .toLowerCase();

    if (!normalizedSearch) {
      return mostViewedPosts;
    }

    return mostViewedPosts.filter(
      (post: PostItem) => {
        const searchableText = [
          post.title,
          post.short_description,
          post.client_name,
          post.category_name,
          post.main_category,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchableText.includes(
          normalizedSearch
        );
      }
    );
  }, [mostViewedPosts, search]);

  const handlePostClick = (
    postId: number
  ) => {
    navigate(`/news-details/${postId}`);
  };

  return (
    <>
      <Header />
      <Leftnav />
      <Rightchat />

      <div
        className="main-content right-chat-active"
        style={{
          paddingLeft: '285px',
          paddingRight: '315px',
          paddingTop: '58px',
          minHeight: '100vh',
          transition: 'all 0.3s ease',
        }}
      >
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="stories-page-wrapper">

              <div className="stories-header-panel">
                <div className="stories-header-row">
                  <div>
                    <h2 className="stories-title">Trending News</h2>
                    <p className="stories-subtitle">
                      Explore the most viewed and popular news from clubs.
                    </p>
                  </div>

                  <span className="stories-count-text">
                    {filteredPosts.length} {filteredPosts.length === 1 ? 'Post' : 'Posts'}
                  </span>
                </div>

                <div className="stories-search-row">
                  <span className="stories-search-icon" aria-hidden="true">⌕</span>
                  <input
                    type="text"
                    className="stories-search-input"
                    placeholder="Search stories, clubs, or keywords..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      className="stories-search-clear"
                      aria-label="Clear search"
                      onClick={() => setSearch('')}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {mostViewedLoading ? (
                <div className="row g-3">
                  {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                    <div className="col-xl-4 col-lg-6 col-md-6" key={index}>
                      <div className="story-card story-skeleton">
                        <div className="story-skeleton-image" />
                        <div className="story-body">
                          <div className="story-skeleton-line story-skeleton-line--sm" />
                          <div className="story-skeleton-line story-skeleton-line--lg" />
                          <div className="story-skeleton-line story-skeleton-line--md" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="stories-state-box stories-error-box">
                  <span className="stories-state-icon">!</span>
                  <p className="mb-0">
                    {error || 'Failed to load trending posts.'}
                  </p>
                </div>
              ) : (
                <div className="row g-3">
                  {filteredPosts.length > 0 ? (
                    filteredPosts.map((post: PostItem) => {
                      const imageUrl = getUploadUrl(
                        post.post_image,
                        post.youtube_url
                      );

                      return (
                        <div
                          className="col-xl-4 col-lg-6 col-md-6"
                          key={post.post_id}
                        >
                          <article
                            className="story-card"
                            role="button"
                            tabIndex={0}
                            onClick={() => handlePostClick(post.post_id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handlePostClick(post.post_id);
                              }
                            }}
                          >
                            <div className="story-image-wrap">
                              <img
                                src={imageUrl}
                                alt={post.title || 'Club post image'}
                                className="story-image"
                                loading="lazy"
                                onError={(event) => {
                                  if (
                                    event.currentTarget.src !==
                                    `${window.location.origin}${FALLBACK_IMAGE}`
                                  ) {
                                    event.currentTarget.src = FALLBACK_IMAGE;
                                  }
                                }}
                              />
                              <div className="story-overlay" />

                              {post.category_name && (
                                <span className="story-category-pill">
                                  {post.category_name}
                                </span>
                              )}

                              <div className="story-view-pill">
                                <span aria-hidden="true" className="story-eye">◉</span>
                                {Number(post.view_count || 0)}
                              </div>
                            </div>

                            <div className="story-body">
                              <div className="story-meta-row">
                                <span className="story-club-name">
                                  {post.client_name || 'Club'}
                                </span>
                                <span className="story-date">
                                  {formatPostDate(post.created_at)}
                                </span>
                              </div>

                              <h4 className="story-title">
                                {post.title || 'Untitled Post'}
                              </h4>

                              {post.short_description && (
                                <p className="story-description">
                                  {post.short_description}
                                </p>
                              )}
                            </div>
                          </article>
                        </div>
                      );
                    })
                  ) : (
                    <div className="col-12">
                      <div className="stories-state-box">
                        <span className="stories-state-icon">⌕</span>
                        <p className="mb-1 fw-semibold">No stories found</p>
                        <p className="mb-0 text-muted">Try another search term.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Appfooter />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

        .main-content.right-chat-active {
          width: 100%;
          font-family: 'Inter', sans-serif;
          background: linear-gradient(180deg, #f8f9fc 0%, #f1f3f8 100%);
        }

        .stories-page-wrapper {
          margin-left: 22px;
          max-width: 900px;
          margin-top: 28px;
          padding-bottom: 40px;
        }

        /* ---------- Header ---------- */

        .stories-header-panel {
          margin-bottom: 18px;
          padding: 14px 16px;
          border: 1px solid #e2eaf6;
          border-radius: 16px;
          background:
            linear-gradient(
              135deg,
              #ffffff 0%,
              #f7faff 58%,
              #f1f6ff 100%
            );
          box-shadow:
            0 5px 16px rgba(10, 35, 84, 0.045);
        }

        .stories-header-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 10px;
        }

        .stories-title {
          font-family: 'Fraunces', serif;
          font-size: 24px;
          font-weight: 700;
          color: #0A2354;
          margin-bottom: 4px;
          letter-spacing: -0.01em;
        }

        .stories-subtitle {
          color: #8a93a3;
          font-size: 13px;
          margin-bottom: 0;
        }

        .stories-count-text {
          display: inline-flex;
          align-items: center;
          min-height: 26px;
          padding: 0 9px;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.78);
          color: #5f7fae;
          font-size: 11px;
          font-weight: 700;
          white-space: nowrap;
        }

        /* ---------- Search ---------- */

        .stories-search-row {
          position: relative;
          max-width: 420px;
          margin-bottom: 0;
        }

        .stories-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #9aa4b2;
          font-size: 14px;
          pointer-events: none;
          line-height: 1;
        }

        .stories-search-input {
          height: 40px;
          width: 100%;
          border-radius: 10px;
          border: 1px solid #e5e8ef;
          background: rgba(255, 255, 255, 0.92);
          font-size: 13px;
          padding: 0 34px 0 36px;
          color: #1f2937;
          box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.05);
          transition: box-shadow 0.2s ease, border-color 0.2s ease;
        }

        .stories-search-input::placeholder {
          color: #9aa4b2;
        }

        .stories-search-input:focus {
          outline: none;
          border-color: #2F6FE4;
          box-shadow:
            inset 0 1px 2px rgba(15, 23, 42, 0.04),
            0 0 0 3px rgba(47, 111, 228, 0.10);
        }

        .stories-search-clear {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          border: none;
          background: #eef1f6;
          color: #64748b;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          font-size: 12px;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .stories-search-clear:hover {
          background: #e2e6ee;
        }

        /* ---------- Grid ---------- */

        .stories-page-wrapper .row {
          --bs-gutter-x: 16px;
          --bs-gutter-y: 16px;
          margin-left: 0;
        }

        /* ---------- Card ---------- */

        .story-card {
          background: #ffffff;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #e9ecf2;
          height: 100%;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
          transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
          cursor: pointer;
          outline: none;
        }

        .story-card:hover,
        .story-card:focus-visible {
          transform: translateY(-2px);
          border-color: #c9d9f8;
          box-shadow: 0 12px 28px rgba(10, 35, 84, 0.12);
        }

        .story-image-wrap {
          position: relative;
          height: 158px;
          overflow: hidden;
          background: #eef1f6;
        }

        .story-image {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.35s ease;
        }

        .story-card:hover .story-image {
          transform: scale(1.04);
        }

        .story-overlay {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: linear-gradient(to top, rgba(10, 35, 84, 0.45), transparent 50%);
        }

        .story-category-pill {
          position: absolute;
          left: 10px;
          bottom: 10px;
          background: rgba(255, 255, 255, 0.92);
          color: #0A2354;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 600;
          backdrop-filter: blur(2px);
        }

        .story-view-pill {
          position: absolute;
          top: 10px;
          right: 10px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: rgba(10, 35, 84, 0.55);
          color: #ffffff;
          padding: 4px 9px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 600;
          backdrop-filter: blur(2px);
        }

        .story-eye {
          font-size: 8px;
        }

        .story-body {
          padding: 14px 14px 16px;
        }

        .story-meta-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 8px;
        }

        .story-club-name {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #2F6FE4;
          font-size: 11.5px;
          font-weight: 600;
        }

        .story-date {
          flex-shrink: 0;
          font-size: 11px;
          color: #9aa4b2;
        }

        .story-title {
          font-family: 'Fraunces', serif;
          display: -webkit-box;
          overflow: hidden;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          min-height: 44px;
          font-size: 15.5px;
          font-weight: 600;
          color: #0f1b34;
          margin-bottom: 6px;
          line-height: 1.4;
        }

        .story-description {
          display: -webkit-box;
          overflow: hidden;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          min-height: 36px;
          font-size: 12.5px;
          line-height: 1.5;
          color: #7c8494;
          margin-bottom: 0;
        }

        /* ---------- Skeleton ---------- */

        .story-skeleton {
          cursor: default;
          pointer-events: none;
        }

        .story-skeleton-image {
          height: 158px;
          background: linear-gradient(90deg, #eef1f6 25%, #f6f8fb 37%, #eef1f6 63%);
          background-size: 400% 100%;
          animation: skeleton-shimmer 1.4s ease infinite;
        }

        .story-skeleton-line {
          height: 10px;
          border-radius: 6px;
          background: linear-gradient(90deg, #eef1f6 25%, #f6f8fb 37%, #eef1f6 63%);
          background-size: 400% 100%;
          animation: skeleton-shimmer 1.4s ease infinite;
          margin-bottom: 10px;
        }

        .story-skeleton-line--sm { width: 40%; height: 8px; }
        .story-skeleton-line--lg { width: 90%; }
        .story-skeleton-line--md { width: 65%; margin-bottom: 0; }

        @keyframes skeleton-shimmer {
          0% { background-position: 100% 0; }
          100% { background-position: -100% 0; }
        }

        /* ---------- Empty / error states ---------- */

        .stories-state-box {
          display: flex;
          min-height: 200px;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border: 1px dashed #dbe2ec;
          border-radius: 16px;
          background: #ffffff;
          color: #7c8494;
          text-align: center;
          padding: 28px;
        }

        .stories-state-icon {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: #eef1f6;
          color: #94a3b8;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .stories-error-box {
          color: #b91c1c;
          border-color: #fbd5d5;
          background: #fff8f8;
        }

        .stories-error-box .stories-state-icon {
          background: #fee2e2;
          color: #dc2626;
        }

        /* ---------- Responsive ---------- */

        @media (max-width: 1400px) {
          .main-content.right-chat-active {
            padding-right: 290px !important;
          }
        }

        @media (max-width: 1200px) {
          .main-content.right-chat-active {
            padding-left: 20px !important;
            padding-right: 20px !important;
          }

          .stories-page-wrapper {
            margin-left: 0;
            max-width: 100%;
          }
        }

        @media (max-width: 768px) {
          .main-content.right-chat-active {
            padding-top: 16px !important;
            padding-left: 12px !important;
            padding-right: 12px !important;
          }

          .middle-sidebar-bottom,
          .middle-sidebar-left {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .stories-page-wrapper {
            margin-top: 0;
            margin-left: 0;
            max-width: 100%;
            padding-bottom: 76px;
          }

          .stories-header-panel {
            padding: 12px 13px;
            border-radius: 14px;
          }

          .stories-search-row {
            max-width: 100%;
          }

          .stories-title {
            font-size: 21px;
          }

          .story-image-wrap,
          .story-skeleton-image {
            height: 200px;
          }

          .story-title {
            min-height: auto;
          }

          .story-description {
            min-height: auto;
          }
        }
      `}</style>
    </>
  );
};

export default Defaultstorie;